import { useEffect, useState } from 'react';

/**
 * Whether the browser currently has network connectivity.
 *
 * The platform already tracks this — `navigator.onLine` plus the window
 * 'online'/'offline' events — so there is no health-check poll here and there
 * shouldn't be one: a poll costs a request per tab per interval to learn
 * something the browser hands us for free, and it's slower to react.
 *
 * Caveat worth knowing: `navigator.onLine === true` only means an interface is
 * up, not that the internet works (captive portals, dead uplink). It's reliable
 * in the negative direction only, which is exactly how it's used — to show a
 * banner when it's false, never to suppress a real error when it's true.
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(
    () => typeof navigator === 'undefined' || navigator.onLine !== false
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    // The events can fire between first render and this effect — resync once.
    setOnline(navigator.onLine !== false);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}
